import { useState } from 'react';

import { useNavigate } from 'react-router-dom';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useRecoilValue } from 'recoil';
import { projectState } from 'state';
import { RoutePaths } from 'types/types';
import { IconButton, TextField, Tooltip } from '@mui/material';
import { Search } from '@mui/icons-material';
import { Project } from 'api/models';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/system';

import { HomeMenuItem } from './Home';
import RouterLink from 'components/RouterLink/RouterLink';
import MuiIcon from 'components/MuiIcon/MuiIcon';
import { grey } from '@mui/material/colors';

interface SideBarItemProps {
  menuItem: HomeMenuItem;
  open: boolean;
  onClick?: () => void;
  isSelected?: boolean;
}

const getMenuItemUrl = (
  routePath: RoutePaths,
  selectedProject?: Project | null,
  query?: Record<string, string>,
) => {
  if (query) {
    const queryStr = Object.entries(query)
      .map(([key, value]) => `${key}=${value}`)
      .join('&');

    return `${routePath.replace(
      ':projectId',
      selectedProject?.id ?? '',
    )}?${queryStr}`;
  }
  return routePath.replace(':projectId', selectedProject?.id ?? '');
};

export default function SideBarItem({
  menuItem,
  open,
  onClick,
  isSelected,
}: SideBarItemProps) {
  const { selectedProject } = useRecoilValue(projectState);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState<string>('');

  const onSearch = () => {
    setSearchText('');
    navigate(
      getMenuItemUrl(
        menuItem.path,
        selectedProject,
        searchText ? { searchText } : undefined,
      ),
    );
  };

  return (
    <Box data-testid={`side-bar-item-${menuItem.id}`} onClick={onClick}>
      {menuItem.path !== RoutePaths.patients ? (
        <RouterLink to={getMenuItemUrl(menuItem.path, selectedProject)}>
          <ListItem disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              selected={isSelected}
              sx={{
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
                px: 2.5,
                color: grey[300],
                '&.Mui-selected': {
                  borderTopRightRadius: '30px',
                  borderBottomRightRadius: '30px',
                  mr: open ? 1 : '4px',
                  background: theme =>
                    `linear-gradient(270deg, ${theme.palette.primary.main}, rgb(60 10 173) 100%)`,
                },
              }}
            >
              <Tooltip title={t(menuItem.name)}>
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  <MuiIcon icon={menuItem.icon} sx={{ color: grey[300] }} />
                </ListItemIcon>
              </Tooltip>

              <ListItemText
                primary={t(menuItem.name)}
                sx={{ opacity: open ? 1 : 0 }}
              />
            </ListItemButton>
          </ListItem>
        </RouterLink>
      ) : (
        <ListItem
          disablePadding
          sx={{
            display: 'block',
            ':active': { backgroundColor: 'transparent' },
            color: grey[300],
          }}
          onClick={e => {
            const target = e.target as HTMLInputElement;
            if (target.type !== 'search') {
              navigate(
                getMenuItemUrl(menuItem.path, selectedProject, {
                  searchText,
                }),
              );
            }
          }}
        >
          <ListItemButton
            sx={{
              minHeight: 48,
              justifyContent: open ? 'initial' : 'center',
              px: 2.5,
              '&.Mui-selected': {
                borderTopRightRadius: '30px',
                borderBottomRightRadius: '30px',
                mr: 1,
                background: theme =>
                  `linear-gradient(270deg, ${theme.palette.primary.main}, rgb(60 10 173) 100%)`,
              },
            }}
            disableRipple
            disableTouchRipple
            selected={isSelected}
          >
            <Tooltip title={t(menuItem.name)}>
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                <MuiIcon icon={menuItem.icon} sx={{ color: grey[300] }} />
              </ListItemIcon>
            </Tooltip>
            {open && (
              <>
                <TextField
                  type="search"
                  label={t('home.menu.patients')}
                  variant="standard"
                  fullWidth
                  size="small"
                  sx={{
                    mb: 1,
                    mr: 2,
                    input: {
                      color: 'white',
                    },

                    '& .MuiInputLabel-root': {
                      color: isSelected ? 'white' : grey[500],
                    },
                  }}
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  onKeyUp={e => e.key === 'Enter' && onSearch()}
                />
                <IconButton
                  size="small"
                  sx={{
                    position: 'absolute',
                    right: 0,
                    mt: 1,
                    mr: 1,
                    color: grey[300],
                  }}
                  onClick={onSearch}
                >
                  <Search
                    fontSize="small"
                    data-testid="search-button"
                    sx={{ fontSize: '15px', color: grey[300] }}
                  />
                </IconButton>
              </>
            )}
          </ListItemButton>
        </ListItem>
      )}
    </Box>
  );
}
